import { useState, useRef, useEffect } from "react";
import { InventoryItem } from "@/hooks/useDocuments";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Hash, FileText, Ruler, Check, X, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { calcSubTotal } from "@/hooks/useInvoiceMath";
import { formatHsCode } from "@/hooks/useComplianceLookup";
import type { ComplianceResult } from "@/hooks/useComplianceLookup";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";

interface Props {
  item: InventoryItem;
  isPacking: boolean;
  onEdit: (item: InventoryItem) => void;
  onDelete: (id: string) => void;
  onSaveItem?: (id: string, update: Record<string, any>) => Promise<boolean>;
  showAudit?: boolean;
  complianceResult?: ComplianceResult;
  landedCostUnitario?: number;
  onOpenCompliance?: () => void;
}

function InfoRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-foreground truncate">{value}</span>
    </div>
  );
}

export function InventoryCardMobile({ item, isPacking, onEdit, onDelete, onSaveItem, showAudit, complianceResult, landedCostUnitario, onOpenCompliance }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [vals, setVals] = useState({
    cantidad: "",
    precio_unitario: "",
    peso_bruto_kg: "",
    descripcion: "",
  });
  const [flash, setFlash] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const prevSubTotal = useRef<number>(0);

  const derivedSubTotal = calcSubTotal(vals.cantidad, vals.precio_unitario);

  useEffect(() => {
    if (!isEditing) return;
    if (derivedSubTotal !== prevSubTotal.current) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 600);
      prevSubTotal.current = derivedSubTotal;
      return () => clearTimeout(t);
    }
  }, [derivedSubTotal, isEditing]);

  const startEditing = () => {
    setIsEditing(true);
    setVals({
      cantidad: String(item.cantidad ?? ""),
      precio_unitario: String(item.precio_unitario ?? ""),
      peso_bruto_kg: String(item.peso_bruto_kg ?? ""),
      descripcion: item.descripcion || "",
    });
    prevSubTotal.current = calcSubTotal(item.cantidad, item.precio_unitario);
  };

  const cancelEditing = () => setIsEditing(false);

  const handleBlur = (field: keyof typeof vals) => {
    if (["cantidad", "precio_unitario", "peso_bruto_kg"].includes(field)) {
      setVals(prev => ({ ...prev, [field]: String(parseFloat(prev[field]) || 0) }));
    }
  };

  const handleChange = (field: keyof typeof vals, value: string) => {
    setVals(prev => ({ ...prev, [field]: value }));
  };

  const saveEditing = async () => {
    if (!onSaveItem) {
      onEdit({ ...item, ...vals, cantidad: parseFloat(vals.cantidad) || 0, precio_unitario: parseFloat(vals.precio_unitario) || 0, peso_bruto_kg: parseFloat(vals.peso_bruto_kg) || 0, sub_total: derivedSubTotal } as any);
      setIsEditing(false);
      return;
    }
    const update: Record<string, any> = {
      cantidad: parseFloat(vals.cantidad) || 0,
      precio_unitario: parseFloat(vals.precio_unitario) || 0,
      peso_bruto_kg: parseFloat(vals.peso_bruto_kg) || 0,
      sub_total: calcSubTotal(vals.cantidad, vals.precio_unitario),
      descripcion: vals.descripcion || null,
    };
    const success = await onSaveItem(item.id, update);
    if (success) setIsEditing(false);
  };

  const regla = complianceResult?.regla;
  const riesgo = (regla?.nivel_riesgo ?? "").toLowerCase();
  const isCritico = riesgo === "crítico" || riesgo === "critico";
  const isAltoMedio = riesgo === "alto" || riesgo === "medio";
  const hasRiesgoDefinido = !!riesgo;
  // B-04 / Fix R6: surface the badge whenever a regulatory rule exists
  // (entidad OR tipo_tramite OR nivel_riesgo) — not only when nivel_riesgo is set.
  const hasCompliance =
    showAudit && !!regla && (!!regla.entidad || !!regla.tipo_tramite || !!regla.nivel_riesgo);
  const badgeLabel = hasRiesgoDefinido ? regla?.nivel_riesgo : "Trámite";
  const complianceData = complianceResult ? { gravamen: complianceResult.gravamen_porcentaje, iva: complianceResult.iva_porcentaje } : null;

  return (
    <>
      <Card className="border-border">
        <CardContent className="p-4 space-y-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-muted-foreground font-mono">{item.no_documento}</p>
              {isEditing ? (
                <Input
                  value={vals.descripcion}
                  onChange={(e) => handleChange("descripcion", e.target.value)}
                  placeholder="Descripción"
                  className="h-8 text-sm mt-0.5"
                />
              ) : (
                <p className="text-sm font-semibold text-foreground mt-0.5 truncate">{item.descripcion || "Sin descripción"}</p>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {hasCompliance && (
                <Badge
                  onClick={() => setDrawerOpen(true)}
                  className={cn(
                    "cursor-pointer text-[10px] px-1.5 py-0.5",
                    isCritico && "bg-destructive text-destructive-foreground",
                    isAltoMedio && "bg-orange-500 text-white",
                    !isCritico && !isAltoMedio && hasRiesgoDefinido && "bg-muted text-muted-foreground",
                    !hasRiesgoDefinido && "bg-blue-500 text-white"
                  )}
                >
                  {badgeLabel}
                </Badge>
              )}
              <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                #{item.item_no}
              </span>
            </div>
          </div>

          {/* Info */}
          <div className="space-y-1.5">
            <InfoRow icon={Hash} label="SKU" value={item.sku} />
            {showAudit && <InfoRow icon={Hash} label="HS Code" value={formatHsCode(item.hs_code || "")} />}
            <InfoRow icon={Ruler} label="Unidad" value={item.unidad_medida} />
          </div>

          {/* Audit data (only when audit mode ON) */}
          {showAudit && complianceData && (item.hs_code ?? "").trim() && (
            <div className="grid grid-cols-3 gap-2 bg-primary/5 rounded-lg px-3 py-2 border border-primary/10">
              <div>
                <p className="text-[10px] text-muted-foreground">Arancel</p>
                <p className="text-sm font-mono font-semibold text-foreground">{complianceData.gravamen}%</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">IVA</p>
                <p className="text-sm font-mono font-semibold text-foreground">{complianceData.iva}%</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Costo Dest.</p>
                <p className="text-sm font-mono font-bold text-primary">
                  {landedCostUnitario && landedCostUnitario > 0
                    ? `$${landedCostUnitario.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : "—"}
                </p>
              </div>
            </div>
          )}

          {/* Editable Quantity & Price */}
          <div className={cn("grid gap-2 bg-muted/50 rounded-lg px-3 py-2", isPacking ? "grid-cols-2" : "grid-cols-3")}>
            <div>
              <p className="text-[10px] text-muted-foreground">Cantidad</p>
              {isEditing ? (
                <Input type="number" inputMode="decimal" value={vals.cantidad} onChange={(e) => handleChange("cantidad", e.target.value)} onBlur={() => handleBlur("cantidad")} className="h-8 text-sm font-mono mt-0.5" />
              ) : (
                <p className="text-sm font-bold font-mono text-foreground">{Number(item.cantidad).toLocaleString()}</p>
              )}
            </div>

            {isPacking && (
              <div>
                <p className="text-[10px] text-muted-foreground">Peso (KG)</p>
                {isEditing ? (
                  <Input type="number" inputMode="decimal" value={vals.peso_bruto_kg} onChange={(e) => handleChange("peso_bruto_kg", e.target.value)} onBlur={() => handleBlur("peso_bruto_kg")} className="h-8 text-sm font-mono mt-0.5" />
                ) : (
                  <p className="text-sm font-mono text-foreground">{Number(item.peso_bruto_kg || 0) > 0 ? Number(item.peso_bruto_kg).toLocaleString() : "—"}</p>
                )}
              </div>
            )}

            {!isPacking && (
              <>
                <div>
                  <p className="text-[10px] text-muted-foreground">P. Unitario</p>
                  {isEditing ? (
                    <Input type="number" inputMode="decimal" value={vals.precio_unitario} onChange={(e) => handleChange("precio_unitario", e.target.value)} onBlur={() => handleBlur("precio_unitario")} className="h-8 text-sm font-mono mt-0.5" />
                  ) : (
                    <p className="text-sm font-mono text-foreground">{Number(item.precio_unitario) > 0 ? `$${Number(item.precio_unitario).toLocaleString()}` : "—"}</p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Subtotal</p>
                  <p className={cn("text-sm font-bold font-mono transition-colors duration-500", isEditing ? "text-primary" : "text-foreground", flash && "!text-primary animate-pulse")}>
                    {isEditing
                      ? `$${derivedSubTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : Number(item.sub_total) > 0 ? `$${Number(item.sub_total).toLocaleString()}` : "—"}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Note */}
          {!isEditing && item.nota_item && (
            <div className="flex items-start gap-2 text-xs">
              <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
              <p className="text-muted-foreground line-clamp-2">{item.nota_item}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-border pt-3">
            <div className="flex items-center gap-1">
              {showAudit && hasCompliance && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 text-xs gap-1.5"
                  onClick={() => onOpenCompliance?.()}
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Documentos
                </Button>
              )}
            </div>
            <div className="flex items-center gap-1">
              {isEditing ? (
                <>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-green-500 hover:text-green-400" onClick={saveEditing}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-foreground" onClick={cancelEditing}>
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-primary" onClick={startEditing}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive" onClick={() => onDelete(item.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compliance Drawer for mobile tap */}
      {hasCompliance && (
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerContent className="max-h-[60vh]">
            <DrawerHeader>
              <DrawerTitle className="text-base">Cumplimiento Normativo</DrawerTitle>
              <DrawerDescription className="text-xs truncate">{item.descripcion}</DrawerDescription>
            </DrawerHeader>
            <div className="px-4 pb-6 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-muted-foreground">Nivel de Riesgo:</span>
                <Badge className={cn(
                  "text-[10px]",
                  isCritico && "bg-destructive text-destructive-foreground",
                  isAltoMedio && "bg-orange-500 text-white",
                  !isCritico && !isAltoMedio && hasRiesgoDefinido && "bg-muted text-muted-foreground",
                  !hasRiesgoDefinido && "bg-blue-500 text-white"
                )}>
                  {hasRiesgoDefinido ? regla!.nivel_riesgo : "Sin nivel definido"}
                </Badge>
              </div>
              <div className="space-y-2 text-sm">
                <p><span className="font-semibold text-muted-foreground">Entidad:</span> {regla!.entidad}</p>
                <p><span className="font-semibold text-muted-foreground">Trámite:</span> {regla!.tipo_tramite}</p>
                <p><span className="font-semibold text-muted-foreground">Regla:</span> {regla!.descripcion_regla}</p>
                {regla!.norma && <p><span className="font-semibold text-muted-foreground">Norma:</span> {regla!.norma}</p>}
              </div>
              <Button
                variant="outline"
                className="w-full h-11 text-sm gap-2 mt-2"
                onClick={() => {
                  setDrawerOpen(false);
                  onOpenCompliance?.();
                }}
              >
                <ShieldCheck className="h-4 w-4" />
                Ver Documentos Soporte
              </Button>
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
}
